// ============================================================================
// HiringRequestCard — propose_hiring_request. Inserts into hiring_requests.
// 2026-05-21 Cowork: AM Co-pilot v2 action expansion.
// ============================================================================

import { useMemo, useState } from 'react';
import { UserPlus } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';
import { FacilityCombobox } from './facility-combobox';

const ROLE_TYPES = [
  'EVS','Food_Service','Greeter','Runner','Security',
  'Night_Manager','Account_Manager','Operations','Executive','Other',
] as const;
const EMPLOYMENT_TYPES = [
  'FT_Salary','Hourly_W2','Hourly_Partner','Fractional_Exec','Contractor_1099',
] as const;
const URGENCIES = ['Emergency', 'Soon', 'Planned'] as const;

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface HiringRequestCardProps {
  proposal: CopilotProposalBase;
  facilities: FacilityOpt[];
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function HiringRequestCard({
  proposal,
  facilities,
  isCommitting,
  onDoIt,
  onDiscard,
}: HiringRequestCardProps) {
  const initial = proposal.input as {
    facility_id?: string;
    title?: string;
    role_type?: string;
    shift?: string;
    employment_type?: string;
    urgency?: string;
    target_start_date?: string | null;
    headcount?: number;
    notes?: string | null;
  };

  const [facility_id, setFacilityId] = useState(initial.facility_id ?? '');
  const [title, setTitle] = useState(initial.title ?? '');
  const [roleType, setRoleType] = useState(initial.role_type ?? 'EVS');
  const [shift, setShift] = useState(initial.shift ?? '');
  const [employmentType, setEmploymentType] = useState(initial.employment_type ?? 'Hourly_W2');
  const [urgency, setUrgency] = useState(initial.urgency ?? 'Soon');
  const [headcount, setHeadcount] = useState(initial.headcount ?? 1);
  const [targetStartDate, setTargetStartDate] = useState(initial.target_start_date ?? '');
  const [notes, setNotes] = useState(initial.notes ?? '');

  const wasEdited = useMemo(() => {
    return (
      facility_id !== (initial.facility_id ?? '') ||
      title !== (initial.title ?? '') ||
      roleType !== (initial.role_type ?? 'EVS') ||
      shift !== (initial.shift ?? '') ||
      employmentType !== (initial.employment_type ?? 'Hourly_W2') ||
      urgency !== (initial.urgency ?? 'Soon') ||
      headcount !== (initial.headcount ?? 1) ||
      (targetStartDate || null) !== (initial.target_start_date ?? null) ||
      (notes || null) !== (initial.notes ?? null)
    );
  }, [
    facility_id, title, roleType, shift, employmentType, urgency,
    headcount, targetStartDate, notes, initial,
  ]);

  const handleDoIt = () => {
    onDoIt(
      {
        facility_id,
        title,
        role_type: roleType,
        shift,
        employment_type: employmentType,
        urgency,
        headcount: Number(headcount),
        target_start_date: targetStartDate || null,
        notes: notes || null,
      },
      wasEdited,
    );
  };

  const urgencyAccent =
    urgency === 'Emergency'
      ? 'border-st-danger-border-strong'
      : urgency === 'Soon'
      ? 'border-st-warn-border-strong'
      : 'border-tcs-border';

  return (
    <CardShell
      title="Hiring Request"
      icon={<UserPlus className="h-4 w-4" />}
      accentClass={urgencyAccent}
      status={proposal.status}
      committedLabel={
        proposal.target_row_id ? `Saved as ${proposal.target_row_id}` : 'Saved'
      }
      isCommitting={isCommitting}
      onDoIt={handleDoIt}
      onDiscard={onDiscard}
    >
      <Field label="Facility">
        <FacilityCombobox value={facility_id} options={facilities} onChange={setFacilityId} />
      </Field>
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Role type">
          <select value={roleType} onChange={(e) => setRoleType(e.target.value)} className={inputClass}>
            {ROLE_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Employment">
          <select value={employmentType} onChange={(e) => setEmploymentType(e.target.value)} className={inputClass}>
            {EMPLOYMENT_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Shift">
        <input value={shift} onChange={(e) => setShift(e.target.value)} className={inputClass} />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Urgency">
          <div className="flex gap-1">
            {URGENCIES.map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setUrgency(u)}
                className={`flex-1 rounded-md px-2 py-1 text-xs font-medium ${
                  urgency === u
                    ? u === 'Emergency'
                      ? 'bg-st-danger-solid text-st-solid-ink'
                      : u === 'Soon'
                      ? 'bg-st-warn-solid text-st-solid-ink'
                      : 'bg-tcs-inverse-soft text-tcs-inverse-ink'
                    : 'bg-tcs-surface text-tcs-text-secondary hover:bg-tcs-surface-strong'
                }`}
              >
                {u}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Headcount">
          <input
            type="number"
            min={1}
            value={headcount}
            onChange={(e) => setHeadcount(Math.max(1, Number(e.target.value) || 1))}
            className={inputClass}
          />
        </Field>
        <Field label="Target start">
          <input
            type="date"
            value={targetStartDate ?? ''}
            onChange={(e) => setTargetStartDate(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          value={notes ?? ''}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={`${inputClass} resize-none`}
        />
      </Field>
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      {children}
    </label>
  );
}
